import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import {
  DollarSign, TrendingUp, Users, MessageSquare, Mic,
  RefreshCw, AlertCircle, AlertTriangle, Save, Loader2, ChevronDown, ChevronRight,
  HandCoins,
} from "lucide-react";

const PROVIDER_COLORS = {
  openai:     "bg-emerald-500",
  openrouter: "bg-sky-500",
  anthropic:  "bg-amber-500",
  tensornex:  "bg-fuchsia-500",
};
const PROVIDER_TEXT = {
  openai:     "text-emerald-600 dark:text-emerald-400",
  openrouter: "text-sky-600 dark:text-sky-400",
  anthropic:  "text-amber-600 dark:text-amber-400",
  tensornex:  "text-fuchsia-600 dark:text-fuchsia-400",
};
const FEATURE_LABEL = {
  chat:            "Chat (text)",
  voice_realtime:  "Voice · Realtime",
  voice_stt:       "Voice · STT (Whisper)",
  voice_tts:       "Voice · TTS",
  voice_thinking:  "Voice · Thinking model",
  voice_diarize:   "Voice · Diarization",
  file_extract:    "File · Extract",
  file_vlm:        "File · Image VLM",
  file_transcribe: "File · Transcribe",
  summary:         "Attachment summary",
};

function fmtUsd(n) {
  if (n == null || isNaN(n)) return "$0.00";
  if (n < 0.01 && n > 0) return "<$0.01";
  return `$${Number(n).toFixed(2)}`;
}

export default function UsageTab() {
  return (
    <div>
      <div className="border border-border bg-card p-6">
        <h3 className="font-display font-bold tracking-tight text-lg">Usage & costs</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Real-time spend telemetry across OpenAI · OpenRouter · Anthropic · Tensornex. All amounts are computed
          from the rates in the Pricing tab — edit them to match your contracts.
        </p>
      </div>

      <Tabs defaultValue="dashboard" className="mt-6">
        <TabsList className="rounded-sm">
          <TabsTrigger value="dashboard" data-testid="usage-dashboard-tab" className="rounded-sm">Dashboard</TabsTrigger>
          <TabsTrigger value="admin-paid" data-testid="usage-admin-paid-tab" className="rounded-sm">Admin-paid</TabsTrigger>
          <TabsTrigger value="by-operation" data-testid="usage-by-operation-tab" className="rounded-sm">By operation</TabsTrigger>
          <TabsTrigger value="pricing" data-testid="usage-pricing-tab" className="rounded-sm">Pricing rates</TabsTrigger>
        </TabsList>
        <TabsContent value="dashboard" className="mt-6">
          <UsageDashboard />
        </TabsContent>
        <TabsContent value="admin-paid" className="mt-6">
          <AdminPaidDashboard />
        </TabsContent>
        <TabsContent value="by-operation" className="mt-6">
          <UsageByOperation />
        </TabsContent>
        <TabsContent value="pricing" className="mt-6">
          <PricingEditor />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ---------------------------------------------------------------------------
function UsageDashboard() {
  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState(null);
  const [daily, setDaily] = useState([]);
  const [topUsers, setTopUsers] = useState([]);
  const [topConvs, setTopConvs] = useState([]);
  const [loading, setLoading] = useState(false);
  // Phase 1 (B5): provider tile list comes from the central registry instead
  // of being hand-typed. Filter to providers that emit cost events
  // (LLM providers + Tensornex which bills via embeddings/extraction).
  // CloudConvert is excluded — it doesn't currently log per-token usage.
  const [usageProviders, setUsageProviders] = useState([
    "openai", "openrouter", "anthropic", "tensornex",
  ]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, d, u, c] = await Promise.all([
        api.get(`/admin/usage/summary?days=${days}`),
        api.get(`/admin/usage/daily?days=${days}`),
        api.get(`/admin/usage/by-user?days=${days}&limit=20`),
        api.get(`/admin/usage/by-conversation?days=${days}&limit=20`),
      ]);
      setSummary(s.data);
      setDaily(d.data);
      setTopUsers(u.data);
      setTopConvs(c.data);
    } catch (err) {
      toast.error("Failed to load usage data");
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  // Phase 1 (B5): fetch the provider registry once and derive which providers
  // get a KPI tile (anything that emits cost events — exclude pure extractors
  // like CloudConvert). Falls back to the hardcoded list if the registry call
  // fails, so the tab keeps rendering.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/admin/providers/registry");
        if (!alive) return;
        const slugs = (data?.providers || [])
          .filter((p) => (p.kinds || []).some((k) => k !== "extractor"))
          .map((p) => p.slug);
        if (slugs.length > 0) setUsageProviders(slugs);
      } catch {/* keep fallback */}
    })();
    return () => { alive = false; };
  }, []);

  const maxDaily = useMemo(
    () => Math.max(0.001, ...daily.map((d) => d.total_usd || 0)),
    [daily],
  );

  return (
    <div className="space-y-6" data-testid="usage-dashboard">
      {/* Controls */}
      <div className="flex items-center gap-2">
        <span className="text-xs uppercase tracking-wider font-mono text-muted-foreground">Window:</span>
        {[7, 30, 90].map((d) => (
          <Button
            key={d}
            size="sm"
            variant={days === d ? "default" : "outline"}
            className="rounded-sm h-8"
            onClick={() => setDays(d)}
            data-testid={`usage-window-${d}`}
          >
            Last {d}d
          </Button>
        ))}
        <Button size="sm" variant="ghost" onClick={load} disabled={loading} className="rounded-sm h-8 ml-auto" data-testid="usage-refresh">
          <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="usage-kpis">
        <KpiTile
          icon={DollarSign}
          label={`${days}-day total`}
          value={fmtUsd(summary?.total_usd || 0)}
          sub={`${summary?.event_count || 0} events`}
          accent
          testid="kpi-total"
        />
        {usageProviders.map((p) => {
          const row = (summary?.by_provider || []).find((x) => x.provider === p);
          return (
            <KpiTile
              key={p}
              icon={TrendingUp}
              label={p}
              value={fmtUsd(row?.total_usd || 0)}
              sub={`${row?.event_count || 0} events`}
              colorClass={PROVIDER_TEXT[p] || "text-muted-foreground"}
              dotClass={PROVIDER_COLORS[p] || "bg-muted-foreground/60"}
              testid={`kpi-${p}`}
            />
          );
        })}
      </div>

      {summary && summary.event_count === 0 && (
        <div className="border border-amber-500/40 bg-amber-500/5 p-4 rounded-sm text-sm flex items-start gap-2">
          <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold text-amber-700 dark:text-amber-300">No usage yet</div>
            <div className="text-muted-foreground mt-1">
              Cost events are logged after the first chat message, voice call, or file upload. Once activity begins, this dashboard will populate within seconds.
            </div>
          </div>
        </div>
      )}

      {/* Daily chart */}
      <div className="border border-border bg-card p-5 rounded-sm" data-testid="usage-daily-chart">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs uppercase tracking-[0.2em] font-mono text-muted-foreground">[ daily cost · last {days} days ]</div>
          <div className="text-xs font-mono text-muted-foreground">Max: {fmtUsd(maxDaily)}</div>
        </div>
        {daily.length === 0 ? (
          <div className="h-32 grid place-items-center text-sm text-muted-foreground">No data in this window.</div>
        ) : (
          <div className="h-40 flex items-end gap-0.5 overflow-x-auto">
            {daily.map((d) => {
              const h = Math.max(2, ((d.total_usd || 0) / maxDaily) * 100);
              return (
                <div
                  key={d.date}
                  className="flex-1 min-w-[6px] flex flex-col justify-end group relative"
                  title={`${d.date}: ${fmtUsd(d.total_usd)}`}
                >
                  <div className="w-full bg-primary/80 hover:bg-primary transition rounded-t-sm" style={{ height: `${h}%` }} />
                  <div className="absolute -top-7 left-1/2 -translate-x-1/2 bg-foreground text-background text-[10px] px-1.5 py-0.5 rounded-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition font-mono z-10">
                    {d.date} · {fmtUsd(d.total_usd)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {daily.length > 0 && (
          <div className="mt-2 flex items-center justify-between text-[11px] font-mono text-muted-foreground">
            <span>{daily[0]?.date}</span>
            <span>{daily[daily.length - 1]?.date}</span>
          </div>
        )}
      </div>

      {/* By feature */}
      <div className="border border-border bg-card p-5 rounded-sm" data-testid="usage-by-feature">
        <div className="text-xs uppercase tracking-[0.2em] font-mono text-muted-foreground mb-3">[ cost by feature ]</div>
        {(summary?.by_feature || []).length === 0 ? (
          <div className="text-sm text-muted-foreground py-3">No feature breakdown yet.</div>
        ) : (
          <ul className="space-y-1.5">
            {(summary?.by_feature || []).map((row) => {
              const pct = Math.max(2, (row.total_usd / Math.max(0.0001, summary.total_usd)) * 100);
              return (
                <li key={row.feature} className="flex items-center gap-3 text-sm">
                  <span className="w-44 truncate text-muted-foreground">{FEATURE_LABEL[row.feature] || row.feature}</span>
                  <div className="flex-1 h-2 bg-secondary rounded-sm overflow-hidden">
                    <div className="h-full bg-foreground" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="w-20 text-right font-mono">{fmtUsd(row.total_usd)}</span>
                  <span className="w-16 text-right font-mono text-[11px] text-muted-foreground">{row.event_count}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Top users + top conversations */}
      <div className="grid lg:grid-cols-2 gap-4">
        <TableCard title="Top users" icon={Users} testid="usage-top-users">
          {topUsers.length === 0 ? (
            <div className="px-4 py-6 text-sm text-muted-foreground">No data.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground">
                <tr>
                  <th className="text-left px-4 py-2 font-normal">User</th>
                  <th className="text-right px-4 py-2 font-normal">Cost</th>
                  <th className="text-right px-4 py-2 font-normal">Events</th>
                </tr>
              </thead>
              <tbody>
                {topUsers.map((u) => (
                  <tr key={u.user_id} className="border-t border-border hover:bg-secondary/30" data-testid={`top-user-${u.user_id}`}>
                    <td className="px-4 py-2">
                      <div className="font-medium truncate">{u.name || u.email}</div>
                      <div className="text-[11px] font-mono text-muted-foreground truncate">{u.email}{u.role === "super_admin" && " · admin"}</div>
                    </td>
                    <td className="px-4 py-2 text-right font-mono font-semibold">{fmtUsd(u.total_usd)}</td>
                    <td className="px-4 py-2 text-right font-mono text-[12px] text-muted-foreground">{u.event_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </TableCard>
        <TableCard title="Top conversations" icon={MessageSquare} testid="usage-top-convs">
          {topConvs.length === 0 ? (
            <div className="px-4 py-6 text-sm text-muted-foreground">No data.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground">
                <tr>
                  <th className="text-left px-4 py-2 font-normal">Conversation</th>
                  <th className="text-right px-4 py-2 font-normal">Cost</th>
                </tr>
              </thead>
              <tbody>
                {topConvs.map((c) => (
                  <tr key={c.conversation_id} className="border-t border-border hover:bg-secondary/30" data-testid={`top-conv-${c.conversation_id}`}>
                    <td className="px-4 py-2">
                      <div className="font-medium truncate flex items-center gap-1.5">
                        {c.is_voice && <Mic className="h-3 w-3 text-primary shrink-0" />}
                        {c.title}
                      </div>
                      <div className="text-[11px] font-mono text-muted-foreground truncate">{c.user_email}</div>
                    </td>
                    <td className="px-4 py-2 text-right font-mono font-semibold">{fmtUsd(c.total_usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </TableCard>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Admin-paid dashboard — subsidized services (Iter 21 Phase 4).
// Consumes /api/admin/usage/admin-subsidized which returns:
//   { days, total_events, total_cost_usd, unique_users,
//     by_feature:[{_id, events, cost_usd}],
//     daily:[{_id (YYYY-MM-DD), cost_usd, events}],
//     top_users:[{_id (user_id), events, cost_usd}] }
// ---------------------------------------------------------------------------
const ADMIN_FEATURE_LABEL = {
  voice_stt_onboarding: "Onboarding voice STT",
};

function AdminPaidDashboard() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [userMap, setUserMap] = useState({});  // user_id -> {email, name}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/usage/admin-subsidized?days=${days}`);
      setData(data);
      // Hydrate top-user emails — best effort, one parallel batch.
      const ids = (data.top_users || []).map((u) => u._id).filter(Boolean);
      if (ids.length) {
        const lookups = await Promise.allSettled(
          ids.map((id) => api.get(`/admin/users/${id}/overview`)),
        );
        const m = {};
        for (let i = 0; i < ids.length; i++) {
          const r = lookups[i];
          if (r.status === "fulfilled" && r.value?.data?.user) {
            m[ids[i]] = { email: r.value.data.user.email, name: r.value.data.user.name };
          }
        }
        setUserMap(m);
      } else {
        setUserMap({});
      }
    } catch {
      toast.error("Failed to load admin-paid usage");
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const maxDaily = useMemo(
    () => Math.max(0.001, ...((data?.daily || []).map((d) => d.cost_usd || 0))),
    [data],
  );
  const totalByFeature = useMemo(() => {
    return Math.max(0.0001, (data?.by_feature || []).reduce((a, r) => a + (r.cost_usd || 0), 0));
  }, [data]);

  return (
    <div className="space-y-6" data-testid="admin-paid-dashboard">
      {/* Controls */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs uppercase tracking-wider font-mono text-muted-foreground">Window:</span>
        {[7, 30, 90].map((d) => (
          <Button
            key={d}
            size="sm"
            variant={days === d ? "default" : "outline"}
            className="rounded-sm h-8"
            onClick={() => setDays(d)}
            data-testid={`admin-paid-window-${d}`}
          >
            Last {d}d
          </Button>
        ))}
        <Button size="sm" variant="ghost" onClick={load} disabled={loading} className="rounded-sm h-8 ml-auto" data-testid="admin-paid-refresh">
          <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <div className="border border-sky-500/30 bg-sky-500/[0.04] p-4 rounded-sm flex items-start gap-3" data-testid="admin-paid-banner">
        <HandCoins className="h-5 w-5 text-sky-600 shrink-0 mt-0.5" />
        <div className="text-sm">
          <div className="font-bold text-sky-700 dark:text-sky-400">Admin-paid services</div>
          <div className="text-muted-foreground mt-0.5">
            Cost absorbed by the admin (not billed to users). Configurable in Admin → Billing → "Auto-inspect" &amp; "Admin-paid voice" settings.
          </div>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="admin-paid-kpis">
        <KpiTile
          icon={DollarSign}
          label={`${days}-day spend`}
          value={fmtUsd(data?.total_cost_usd || 0)}
          sub={`${data?.total_events || 0} events`}
          accent
          testid="admin-paid-kpi-total"
        />
        <KpiTile
          icon={Users}
          label="Unique users"
          value={data?.unique_users ?? 0}
          sub="atendidos"
          testid="admin-paid-kpi-users"
        />
        <KpiTile
          icon={Mic}
          label="Features active"
          value={(data?.by_feature || []).length}
          sub="categorías de gasto"
          testid="admin-paid-kpi-features"
        />
      </div>

      {data && data.total_events === 0 && (
        <div className="border border-amber-500/40 bg-amber-500/5 p-4 rounded-sm text-sm flex items-start gap-2">
          <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold text-amber-700 dark:text-amber-300">No admin-paid usage yet</div>
            <div className="text-muted-foreground mt-1">
              Admin-paid events are recorded when users speak during onboarding (or any other admin-subsidized flow you enable). This panel will populate as soon as the first event fires.
            </div>
          </div>
        </div>
      )}

      {/* Daily chart */}
      <div className="border border-border bg-card p-5 rounded-sm" data-testid="admin-paid-daily">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs uppercase tracking-[0.2em] font-mono text-muted-foreground">[ daily admin spend · last {days} days ]</div>
          <div className="text-xs font-mono text-muted-foreground">Max: {fmtUsd(maxDaily)}</div>
        </div>
        {(data?.daily || []).length === 0 ? (
          <div className="h-32 grid place-items-center text-sm text-muted-foreground">No data in this window.</div>
        ) : (
          <div className="h-40 flex items-end gap-0.5 overflow-x-auto">
            {data.daily.map((d) => {
              const h = Math.max(2, ((d.cost_usd || 0) / maxDaily) * 100);
              return (
                <div
                  key={d._id}
                  className="flex-1 min-w-[6px] flex flex-col justify-end group relative"
                  title={`${d._id}: ${fmtUsd(d.cost_usd)} · ${d.events} ev`}
                >
                  <div className="w-full bg-sky-500/80 hover:bg-sky-500 transition rounded-t-sm" style={{ height: `${h}%` }} />
                  <div className="absolute -top-7 left-1/2 -translate-x-1/2 bg-foreground text-background text-[10px] px-1.5 py-0.5 rounded-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition font-mono z-10">
                    {d._id} · {fmtUsd(d.cost_usd)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {(data?.daily || []).length > 0 && (
          <div className="mt-2 flex items-center justify-between text-[11px] font-mono text-muted-foreground">
            <span>{data.daily[0]?._id}</span>
            <span>{data.daily[data.daily.length - 1]?._id}</span>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* By feature */}
        <div className="border border-border bg-card p-5 rounded-sm" data-testid="admin-paid-by-feature">
          <div className="text-xs uppercase tracking-[0.2em] font-mono text-muted-foreground mb-3">[ cost by feature ]</div>
          {(data?.by_feature || []).length === 0 ? (
            <div className="text-sm text-muted-foreground py-3">No feature breakdown yet.</div>
          ) : (
            <ul className="space-y-1.5">
              {data.by_feature.map((row) => {
                const pct = Math.max(2, (row.cost_usd / totalByFeature) * 100);
                return (
                  <li key={row._id} className="flex items-center gap-3 text-sm">
                    <span className="w-44 truncate text-muted-foreground">{ADMIN_FEATURE_LABEL[row._id] || row._id}</span>
                    <div className="flex-1 h-2 bg-secondary rounded-sm overflow-hidden">
                      <div className="h-full bg-sky-500" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="w-20 text-right font-mono">{fmtUsd(row.cost_usd)}</span>
                    <span className="w-16 text-right font-mono text-[11px] text-muted-foreground">{row.events} ev</span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Top consumers */}
        <TableCard title="Top consumers" icon={Users} testid="admin-paid-top-users">
          {(data?.top_users || []).length === 0 ? (
            <div className="px-4 py-6 text-sm text-muted-foreground">No data.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground">
                <tr>
                  <th className="text-left px-4 py-2 font-normal">User</th>
                  <th className="text-right px-4 py-2 font-normal">Cost</th>
                  <th className="text-right px-4 py-2 font-normal">Events</th>
                </tr>
              </thead>
              <tbody>
                {data.top_users.map((u) => {
                  const meta = userMap[u._id];
                  return (
                    <tr key={u._id} className="border-t border-border hover:bg-secondary/30" data-testid={`admin-paid-user-${u._id}`}>
                      <td className="px-4 py-2">
                        <div className="font-medium truncate">{meta?.name || meta?.email || u._id.slice(0, 8)}</div>
                        <div className="text-[11px] font-mono text-muted-foreground truncate">{meta?.email || u._id}</div>
                      </td>
                      <td className="px-4 py-2 text-right font-mono font-semibold">{fmtUsd(u.cost_usd)}</td>
                      <td className="px-4 py-2 text-right font-mono text-[12px] text-muted-foreground">{u.events}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </TableCard>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function KpiTile({ icon: Icon, label, value, sub, accent, colorClass, dotClass, testid }) {
  return (
    <div
      className={`border rounded-sm p-3 ${accent ? "border-primary/40 bg-primary/5" : "border-border bg-card"}`}
      data-testid={testid}
    >
      <div className="flex items-center gap-1.5 mb-1.5">
        {dotClass && <span className={`h-2 w-2 rounded-full ${dotClass}`}></span>}
        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
        <span className={`text-[10px] uppercase tracking-[0.2em] font-mono ${colorClass || "text-muted-foreground"}`}>{label}</span>
      </div>
      <div className="font-display font-extrabold tracking-tight text-2xl">{value}</div>
      <div className="text-[11px] font-mono text-muted-foreground mt-0.5">{sub}</div>
    </div>
  );
}

function TableCard({ title, icon: Icon, testid, children }) {
  return (
    <div className="border border-border bg-card rounded-sm overflow-hidden" data-testid={testid}>
      <div className="px-4 py-2.5 border-b border-border flex items-center gap-2">
        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-xs uppercase tracking-[0.2em] font-mono text-muted-foreground">{title}</span>
      </div>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
function PricingEditor() {
  const [rows, setRows] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [savingKey, setSavingKey] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [overwriteManual, setOverwriteManual] = useState(false);
  const [filterQ, setFilterQ] = useState({});  // per-provider search
  const [collapsed, setCollapsed] = useState({}); // provider -> bool

  const load = async () => {
    try {
      const { data } = await api.get("/admin/pricing");
      setRows(data);
      setDrafts({});
      // Auto-collapse any provider with >50 rates on first load so smaller
      // providers (like Tensornex with 5) are visible without scrolling.
      setCollapsed((prev) => {
        const next = { ...prev };
        const counts = {};
        for (const r of data) counts[r.provider] = (counts[r.provider] || 0) + 1;
        for (const [p, n] of Object.entries(counts)) {
          if (next[p] === undefined && n > 50) next[p] = true;
        }
        return next;
      });
    } catch {
      toast.error("Failed to load pricing");
    }
  };
  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const g = {};
    for (const r of rows) (g[r.provider] = g[r.provider] || []).push(r);
    return g;
  }, [rows]);

  const handleEdit = (r, newVal) => {
    setDrafts((d) => ({ ...d, [`${r.provider}/${r.key}`]: newVal }));
  };

  const handleSave = async (r) => {
    const k = `${r.provider}/${r.key}`;
    const draft = drafts[k];
    const value = parseFloat(draft);
    if (isNaN(value) || value < 0) return toast.error("Enter a non-negative number");
    setSavingKey(k);
    try {
      await api.put(`/admin/pricing/${r.provider}/${encodeURIComponent(r.key)}`, { rate_usd: value });
      toast.success("Updated · this row is now marked 'manual'");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSavingKey(null);
    }
  };

  const handleSeed = async () => {
    if (!window.confirm("Re-insert any missing default pricing rows? Existing rows are preserved.")) return;
    setSeeding(true);
    try {
      await api.post("/admin/pricing/seed-defaults");
      toast.success("Defaults seeded");
      await load();
    } catch {
      toast.error("Seed failed");
    } finally {
      setSeeding(false);
    }
  };

  const handleFetch = async () => {
    if (overwriteManual && !window.confirm("This will OVERWRITE any rates you've manually edited. Continue?")) return;
    setFetching(true);
    try {
      const { data } = await api.post("/admin/pricing/fetch", { overwrite_manual: overwriteManual });
      const okCount = data.total_updated;
      const skip = data.total_skipped_manual;
      toast.success(`${okCount} rates updated · ${skip} manual rates preserved`);
      const errs = Object.entries(data.by_provider).filter(([, v]) => v.ok === false);
      for (const [p, v] of errs) toast.error(`${p}: ${v.error}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fetch failed");
    } finally {
      setFetching(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="pricing-editor">
      <div className="border border-border bg-card rounded-sm p-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[280px]">
            <div className="font-semibold text-sm">Live pricing sync</div>
            <p className="text-xs text-muted-foreground mt-1">
              Pulls per-model rates: OpenRouter from <code className="font-mono">/v1/models</code> (live),
              OpenAI &amp; Anthropic from a curated static catalog. Manually-edited rows are <strong>protected</strong> from being overwritten unless you tick the box below.
            </p>
            <label className="mt-2 flex items-center gap-2 text-xs font-mono select-none cursor-pointer" data-testid="pricing-overwrite-checkbox">
              <input
                type="checkbox"
                checked={overwriteManual}
                onChange={(e) => setOverwriteManual(e.target.checked)}
                className="h-3.5 w-3.5"
              />
              Overwrite manual edits
            </label>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={handleFetch} disabled={fetching} className="rounded-sm" data-testid="pricing-fetch-button">
              {fetching ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
              {fetching ? "Fetching…" : "Fetch current prices"}
            </Button>
            <Button variant="outline" onClick={handleSeed} disabled={seeding} className="rounded-sm" data-testid="pricing-seed-defaults">
              {seeding ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
              Add missing defaults
            </Button>
          </div>
        </div>
      </div>

      {Object.entries(grouped).map(([provider, items]) => {
        const q = (filterQ[provider] || "").toLowerCase();
        const filtered = q
          ? items.filter((r) => r.key.toLowerCase().includes(q) || (r.label || "").toLowerCase().includes(q))
          : items;
        const fetched = items.filter((r) => r.source === "fetched").length;
        const manual = items.filter((r) => r.source === "manual").length;
        const defaults = items.filter((r) => !r.source || r.source === "default").length;
        const isCollapsed = !!collapsed[provider];
        return (
          <div key={provider} className="border border-border bg-card rounded-sm overflow-hidden">
            <button
              type="button"
              onClick={() => setCollapsed((c) => ({ ...c, [provider]: !c[provider] }))}
              className="w-full px-4 py-2.5 border-b border-border flex items-center gap-2 flex-wrap hover:bg-secondary/40 transition text-left"
              data-testid={`pricing-section-toggle-${provider}`}
            >
              {isCollapsed
                ? <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />}
              <span className={`h-2 w-2 rounded-full ${PROVIDER_COLORS[provider] || "bg-foreground"}`}></span>
              <span className={`text-xs uppercase tracking-[0.2em] font-mono ${PROVIDER_TEXT[provider] || ""}`}>{provider}</span>
              <span className="text-xs font-mono text-muted-foreground">· {items.length} rates</span>
              <div className="flex items-center gap-1 ml-2 text-[10px] font-mono">
                {fetched > 0 && <span className="px-1.5 py-0.5 border border-emerald-500/40 text-emerald-700 dark:text-emerald-400 rounded-sm">{fetched} fetched</span>}
                {manual > 0 && <span className="px-1.5 py-0.5 border border-sky-500/40 text-sky-700 dark:text-sky-400 rounded-sm">{manual} manual</span>}
                {defaults > 0 && <span className="px-1.5 py-0.5 border border-border text-muted-foreground rounded-sm">{defaults} default</span>}
              </div>
              <span
                onClick={(e) => e.stopPropagation()}
                className="ml-auto"
              >
                <Input
                  placeholder={`Search ${provider}…`}
                  value={filterQ[provider] || ""}
                  onChange={(e) => setFilterQ((f) => ({ ...f, [provider]: e.target.value }))}
                  className="h-7 w-48 rounded-sm text-xs"
                  data-testid={`pricing-search-${provider}`}
                />
              </span>
            </button>
            {!isCollapsed && (
            <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground sticky top-0 bg-card">
                  <tr>
                    <th className="text-left px-4 py-2 font-normal">Key / Model</th>
                    <th className="text-left px-4 py-2 font-normal">Label</th>
                    <th className="text-left px-4 py-2 font-normal">Unit</th>
                    <th className="text-left px-4 py-2 font-normal">Source</th>
                    <th className="text-left px-4 py-2 font-normal">Rate (USD)</th>
                    <th className="text-right px-4 py-2 font-normal">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-6 text-center text-sm text-muted-foreground">
                        No rows match "{q}"
                      </td>
                    </tr>
                  )}
                  {filtered.map((r) => {
                    const k = `${r.provider}/${r.key}`;
                    const draft = drafts[k];
                    const dirty = draft !== undefined && parseFloat(draft) !== r.rate_usd;
                    const source = r.source || "default";
                    return (
                      <tr key={k} className="border-t border-border" data-testid={`pricing-row-${r.provider}-${r.key}`}>
                        <td className="px-4 py-2 font-mono text-xs">{r.key}</td>
                        <td className="px-4 py-2 text-muted-foreground">{r.label || "—"}</td>
                        <td className="px-4 py-2 font-mono text-xs text-muted-foreground">{r.unit}</td>
                        <td className="px-4 py-2">
                          <SourceBadge source={source} fetchedAt={r.fetched_at} />
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-1">
                            <span className="font-mono text-muted-foreground">$</span>
                            <Input
                              type="number"
                              step="0.000001"
                              min="0"
                              defaultValue={r.rate_usd}
                              key={`${k}-${r.rate_usd}`}   /* re-mount after save */
                              onChange={(e) => handleEdit(r, e.target.value)}
                              className="h-7 w-32 rounded-sm font-mono text-xs"
                              data-testid={`pricing-input-${r.provider}-${r.key}`}
                            />
                          </div>
                        </td>
                        <td className="px-4 py-2 text-right">
                          <Button
                            size="sm"
                            variant={dirty ? "default" : "ghost"}
                            disabled={!dirty || savingKey === k}
                            onClick={() => handleSave(r)}
                            className="h-7 rounded-sm"
                            data-testid={`pricing-save-${r.provider}-${r.key}`}
                          >
                            {savingKey === k ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function SourceBadge({ source, fetchedAt }) {
  if (source === "fetched") {
    // Stale if older than 30 days
    let stale = false;
    if (fetchedAt) {
      stale = (Date.now() - new Date(fetchedAt).getTime()) > 30 * 86400_000;
    }
    return (
      <span
        className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 border rounded-sm ${
          stale
            ? "border-amber-500/40 text-amber-600 dark:text-amber-400"
            : "border-emerald-500/40 text-emerald-700 dark:text-emerald-400"
        }`}
        title={fetchedAt || ""}
      >
        {stale ? "stale" : "fetched"}
      </span>
    );
  }
  if (source === "manual") {
    return <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 border border-sky-500/40 text-sky-700 dark:text-sky-400 rounded-sm">manual</span>;
  }
  return <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 border border-border text-muted-foreground rounded-sm">default</span>;
}


// ===========================================================================
// PHASE 3 — Per-operation cost breakdown
// ===========================================================================
function UsageByOperation() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [month, setMonth] = React.useState(() => {
    const d = new Date();
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
  });

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/usage/by-operation?month=${month}`);
      setData(data);
    } finally {
      setLoading(false);
    }
  }, [month]);
  React.useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="text-muted-foreground py-12 text-center">
        <Loader2 className="h-5 w-5 animate-spin inline-block mr-2" /> Loading...
      </div>
    );
  }
  if (!data) return null;

  // Roll up by operation_slug
  const byOp = {};
  for (const r of data.operations || []) {
    const key = r.operation || "untagged";
    if (!byOp[key]) {
      byOp[key] = { operation: key, cost_usd: 0, sell_usd: 0, events: 0,
                    tokens_in: 0, tokens_out: 0,
                    budget_cap_usd: r.budget_cap_usd, over_budget: false,
                    rows: [] };
    }
    byOp[key].cost_usd += r.cost_usd || 0;
    byOp[key].sell_usd += r.sell_usd || 0;
    byOp[key].events += r.events || 0;
    byOp[key].tokens_in += r.tokens_in || 0;
    byOp[key].tokens_out += r.tokens_out || 0;
    if (r.over_budget) byOp[key].over_budget = true;
    byOp[key].rows.push(r);
  }
  const ops = Object.values(byOp).sort((a, b) => b.cost_usd - a.cost_usd);
  const maxCost = Math.max(1e-6, ...ops.map((o) => o.cost_usd));

  return (
    <div className="space-y-6" data-testid="usage-by-operation">
      <div className="flex items-end justify-between gap-4 border-b border-border pb-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground font-mono">
            [ per-operation breakdown ]
          </div>
          <h3 className="font-display font-black tracking-tight text-xl mt-1.5">
            Where the tokens are going
          </h3>
          <p className="text-sm text-muted-foreground mt-1.5">
            Cost split per background operation (auto-inspect, title gen, embeddings, voice STT, etc.).
            Budget caps trigger automatic fallback to the configured backup model.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground mb-1.5">Month</div>
            <input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="border border-border bg-background px-2 py-1 rounded-sm font-mono text-xs h-9"
              data-testid="usage-by-op-month"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <KpiTile icon={DollarSign} label="Spend" value={fmtUsd(data.total_cost_usd)} sub={`${ops.length} operations`} />
        <KpiTile icon={TrendingUp} label="Charged to users" value={fmtUsd(data.total_sell_usd)} sub="(with markup)" />
        <KpiTile icon={AlertTriangle} label="Untagged" value={fmtUsd(data.unassigned_cost_usd)} sub="legacy / pre-Phase-1 events" />
      </div>

      <div className="space-y-px bg-border border border-border">
        {ops.map((op) => (
          <div key={op.operation} className="bg-card p-4" data-testid={`op-cost-row-${op.operation}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-sm font-bold">
                  {op.operation === "untagged" ? "(untagged / legacy)" : op.operation}
                </span>
                {op.over_budget && (
                  <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-red-950/40 border border-red-700/40 text-red-300">
                    over budget · fallback active
                  </span>
                )}
                {op.budget_cap_usd && !op.over_budget && (
                  <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                    cap {fmtUsd(op.budget_cap_usd)}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-4 text-xs font-mono">
                <span className="text-muted-foreground">{op.events} events</span>
                <span className="text-emerald-300">{fmtUsd(op.cost_usd)}</span>
              </div>
            </div>
            <div className="h-2 bg-muted/30 rounded-sm overflow-hidden">
              <div
                className={`h-full ${op.over_budget ? "bg-red-500" : "bg-emerald-500"}`}
                style={{ width: `${Math.max(2, (op.cost_usd / maxCost) * 100)}%` }}
              />
            </div>
            {op.rows.length > 0 && (
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-[11px] font-mono text-muted-foreground">
                {op.rows.map((r, i) => (
                  <div key={i} className="flex justify-between gap-2 truncate">
                    <span className="truncate">{r.provider} / {r.model_id}</span>
                    <span>{fmtUsd(r.cost_usd)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
        {ops.length === 0 && (
          <div className="bg-card p-8 text-center text-muted-foreground text-sm">
            No usage events recorded for {month}.
          </div>
        )}
      </div>
    </div>
  );
}

